import { useEffect, useRef, useState, type FormEvent } from 'react';
import { OFF } from '../lib/off';
import { useNavigate } from 'react-router';
import { Icon } from '../lib/icons';
import { announceHere } from '../lib/changes';
import { clockOf, dismissVoicemail, heardVoicemail, playedVoicemail, useVoicemailNotices, type VoicemailNotice } from '../lib/voicemail';
import { dismissMissed, useMissedNotices, type MissedNotice } from '../lib/missed';
import { declineRing, joinRing, stopRing, usePatchRings, type PatchRing } from '../lib/patchRing';
import { patchIn, usePatch } from '../lib/patch';
import { controlPhoneCall, dismissPhoneCallProblem, usePhoneCalls, type PhoneCallsState } from '../lib/phoneCalls';
import { api } from '../api/client';
import { usePhone } from './PhoneApp';
import { usePictureOfExtension } from '../lib/pictures';
import { Face } from './PhotoEditor';
import {
  addToCall,
  answer,
  anyoneTalking,
  bringIntoPatch,
  callGroups,
  CONFERENCE_MAX,
  cancelTransfer,
  clockText,
  completeTransfer,
  confKey,
  consult,
  coworkers,
  decline,
  dial,
  dialable,
  dismissCallNotice,
  endForEveryone,
  endingHere,
  hangup,
  hold,
  holdOrResume,
  isMine,
  leaveCall,
  leftCall,
  loadCoworkers,
  peopleOn,
  prettyNumber,
  pressKey,
  removeFromCall,
  showAdd,
  showKeypad,
  showSound,
  showTransfer,
  startCallSound,
  switchTo,
  toggleMute,
  transfer,
  useCallClock,
  useCalls,
  whoOf,
  type CallsState
} from '../lib/calls';
import { chooseDevice, choices, chosenValue, useAudioDevices } from '../lib/audioDevices';
import { namesText } from '../lib/patchText';
import type { AppCall, DeskInPatch, PhoneCallView } from '../api/client';
import { useAuth } from '../lib/auth';
import { CALL_FOLD, callBarLabel, showCallBar, useFold } from '../lib/fold';
import { Button } from './ui';
import { AskStitch } from './AskStitch';

/**
 * Calls in the app, wherever the person is: a call ringing (Answer, Decline), the call this device
 * has (mute, keypad, hold, add someone, hang up, with its clock; with several people on it, everyone
 * listed), a call that's on another of their devices, one they left that's going on without them,
 * and what happened to one that ended. A call on their desk phone shows too, with Hold and Hang up.
 * New voicemail shows here as well, with Play. In the top corner on a computer, across the top on a
 * phone. Marked private: live help and session recordings leave it out.
 */
export function CallDock() {
  const s = useCalls();
  const { state: auth } = useAuth();
  const voicemail = useVoicemailNotices();
  const missedCalls = useMissedNotices();
  const patchRings = usePatchRings();
  const phone = usePhoneCalls();
  const [folded, setFolded] = useFold(CALL_FOLD);
  if (!auth?.signedIn) return null;
  const deskCalls = phone.tenant ? phone.calls : [];
  const deskInPatch = phone.tenant ? phone.patches : [];
  // A call this device just ended for everyone is on its way out: nothing shows it.
  const calls = s.tenant ? s.calls.filter((c) => !endingHere(c)) : [];
  const ringing = calls.filter((c) => c.direction === 'inbound' && c.state === 'ringing');
  // The call this device has the sound for, then any other of its calls (on hold, or a transfer's caller).
  const mine = calls.find((c) => c.id === s.mine) ?? calls.find((c) => isMine(c) && c.state !== 'ringing');
  // Several people on this device's call: all of them, as one call.
  const people = mine ? peopleOn(confKey(mine), calls) : [];
  const others = calls.filter((c) => !mine || confKey(c) !== confKey(mine));
  const parked = callGroups(others.filter((c) => isMine(c) && c.state === 'active'));
  const elsewhere = callGroups(others.filter((c) => (c.state === 'active' || c.state === 'dialing') && !isMine(c) && !leftCall(c)));
  // A call with several people that this person left: going on without them, on their line.
  const left = callGroups(others.filter((c) => leftCall(c) && (c.state === 'active' || c.state === 'dialing')));
  const notice = s.tenant ? s.notice : null;
  if (!ringing.length && !mine && !parked.length && !elsewhere.length && !left.length && !deskCalls.length && !deskInPatch.length && !phone.problem && !notice && !voicemail.length && !missedCalls.length && !patchRings.length) return null;
  const waiting = parked.filter((g) => !g.some((c) => c.id === mine?.consultFor));
  // The card folds to one bar for a connected call, when nothing on it needs the person (a call
  // ringing in has its own card, so Answer is never folded away). Calls on hold wait under the bar.
  const fold = {
    connected: people.length > 1 ? people.some((c) => c.state === 'active') : mine?.state === 'active',
    transferring: Boolean(mine?.consultFor),
    needsClick: s.needsClick,
    micProblem: Boolean(s.micProblem)
  };
  const canFold = showCallBar({ folded: true, ...fold });
  const bar = Boolean(mine) && showCallBar({ folded, ...fold });
  const foldCard = (on: boolean) => {
    // The other side's arrow takes the focus; the card's panels close as it folds.
    callFocus.current = true;
    if (on) {
      showKeypad(false);
      showTransfer(false);
      showAdd(false);
      showSound(false);
    }
    setFolded(on);
  };
  // Live sessions (1.13.0): calls and Patches ring on one card, with the kind on it.
  const sessions = Boolean(auth.memberships?.some((m) => m.liveSessions && (m.tenant === s.tenant || patchRings.some((r) => r.tenant === m.tenant))));
  return (
    <div className="call-dock" role="region" aria-label="Calls" data-private="">
      {sessions ? (
        <>
          {ringing.map((c) => (c.patchOffer ? <PatchCallBack key={c.id} call={c} s={s} /> : <Ringing key={c.id} kind="call" call={c} s={s} />))}
          {patchRings.map((r) => (r.quiet ? <PatchStarted key={r.key} r={r} /> : <Ringing key={r.key} kind="patch" ring={r} s={s} />))}
        </>
      ) : (
        <>
          {ringing.map((c) => (
            <Incoming key={c.id} call={c} s={s} />
          ))}
          {patchRings.map((r) => (r.quiet ? <PatchStarted key={r.key} r={r} /> : <PatchRinging key={r.key} r={r} s={s} />))}
        </>
      )}
      {mine &&
        (bar ? (
          <CallBar call={mine} people={people} s={s} waiting={waiting.length} onOpen={() => foldCard(false)} />
        ) : people.length > 1 ? (
          <OnConference people={people} s={s} onFold={canFold ? () => foldCard(true) : undefined} />
        ) : (
          <OnCall call={mine} s={s} caller={mine.consultFor ? s.calls.find((c) => c.id === mine.consultFor) : undefined} onFold={canFold ? () => foldCard(true) : undefined} />
        ))}
      {!bar &&
        waiting.map((g) => (
          <Parked key={confKey(g[0]!)} people={g} s={s} />
        ))}
      {!mine &&
        elsewhere.map((g) => (
          <Elsewhere key={confKey(g[0]!)} people={g} />
        ))}
      {left.map((g) => (
        <LeftCall key={confKey(g[0]!)} people={g} s={s} />
      ))}
      {deskCalls.map((c) => (
        <DeskCall key={c.id} c={c} p={phone} />
      ))}
      {deskInPatch.map((d) => (
        <DeskPatch key={d.patchId} d={d} tenant={phone.tenant} />
      ))}
      {phone.problem && (
        <div className="call-card call-notice" role="alert">
          <span>{phone.problem}</span>
          <button type="button" className="call-x" aria-label="Dismiss" onClick={dismissPhoneCallProblem}>
            <Icon name="x" size={15} />
          </button>
        </div>
      )}
      {notice && (
        <div className="call-card call-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="call-x" aria-label="Dismiss" onClick={dismissCallNotice}>
            <Icon name="x" size={15} />
          </button>
        </div>
      )}
      {voicemail.map((n) => (
        <NewVoicemail key={`${n.tenant}|${n.messageId}`} n={n} />
      ))}
      {missedCalls.map((n) => (
        <MissedCall key={`${n.tenant}|${n.callId}`} n={n} canCall={Boolean(s.on && s.available && s.tenant === n.tenant)} />
      ))}
    </div>
  );
}

/**
 * A Patch from a direct message, ringing: Join or Decline. On a call in the app, Join holds it
 * first; in another Patch, it leaves that one.
 */
function PatchRinging({ r, s }: { r: PatchRing; s: CallsState }) {
  const [busy, setBusy] = useState(false);
  const patch = usePatch();
  const onCall = s.calls.find((c) => c.id === s.mine && c.state === 'active' && !c.held);
  const inOther = patch.phase === 'joining' || patch.phase === 'connected' || patch.phase === 'reconnecting';
  const join = async () => {
    setBusy(true);
    try {
      if (onCall) await hold(onCall.id, true);
      await joinRing(r);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="call-card call-incoming patch-ring" role="alertdialog" aria-label={`${r.by.name} is patching you in`}>
      <div className="call-head">
        <span className="call-ring" aria-hidden="true">
          <Icon name="headset" size={17} />
        </span>
        <span className="call-what">{onCall || inOther ? 'Patch waiting' : 'Patch'}</span>
      </div>
      <span className="call-who-row">
        <Face userId={r.by.id} name={r.by.name} size="small" className="call-face" />
        <span className="call-who">
          <strong>{r.by.name}</strong>
          <span>is patching you in</span>
        </span>
      </span>
      <div className="call-actions">
        <Button variant="danger" icon="x" className="call-decline" disabled={busy} onClick={() => void declineRing(r)}>
          Decline
        </Button>
        <Button variant="primary" icon="headset" className="call-answer" disabled={busy} onClick={() => void join()}>
          {busy ? 'Joining…' : onCall ? 'Hold and join' : inOther ? 'Leave and join' : 'Join'}
        </Button>
      </div>
    </div>
  );
}

/**
 * One incoming card for calls and Patches (Live sessions, 1.13.0): "Call from (410) 555-0100" or
 * "Patch from Sam", with the kind on it. Answer routes by the kind: a call is answered on its line
 * (holding a call this device has); a Patch is joined (holding a call, or leaving another Patch).
 */
function Ringing(props: { kind: 'call'; call: AppCall; s: CallsState } | { kind: 'patch'; ring: PatchRing; s: CallsState }) {
  const { s } = props;
  const [joining, setJoining] = useState(false);
  const patch = usePatch();
  const onCall = s.calls.find((c) => c.id === s.mine && c.state === 'active' && !c.held);
  const inPatch = patch.phase === 'joining' || patch.phase === 'connected' || patch.phase === 'reconnecting';
  const isCall = props.kind === 'call';
  const name = isCall ? whoOf(props.call) : props.ring.by.name;
  const waiting = isCall ? props.call.waiting || Boolean(s.mine) || inPatch : Boolean(onCall) || inPatch;
  const busy = isCall ? s.busy !== null : joining;
  const answerIt = async () => {
    if (props.kind === 'call') return answer(props.call.id);
    setJoining(true);
    try {
      if (onCall) await hold(onCall.id, true);
      await joinRing(props.ring);
    } finally {
      setJoining(false);
    }
  };
  const label = isCall
    ? s.busy === `answer:${props.call.id}`
      ? 'Answering…'
      : s.mine
        ? 'Hold and answer'
        : 'Answer'
    : joining
      ? 'Joining…'
      : onCall
        ? 'Hold and answer'
        : inPatch
          ? 'Leave and answer'
          : 'Answer';
  const what = `${isCall ? 'Call' : 'Patch'} from ${name}`;
  return (
    <div className={`call-card call-incoming ring-${props.kind}`} role="alertdialog" aria-label={what}>
      <div className="call-head">
        <span className="call-ring" aria-hidden="true">
          <Icon name={isCall ? 'phone' : 'headset'} size={17} />
        </span>
        <span className="call-what">{`${isCall ? 'Call' : 'Patch'}${waiting ? ' waiting' : ''}`}</span>
      </div>
      {isCall ? (
        <Who call={props.call} tenant={s.tenant} />
      ) : (
        <span className="call-who-row">
          <Face userId={props.ring.by.id} name={name} size="small" className="call-face" />
          <span className="call-who">
            <strong>{name}</strong>
            <span>Patch in Stitch</span>
          </span>
        </span>
      )}
      {isCall && props.call.forwardedFrom && (
        <span className="call-sub">Forwarded from {props.call.forwardedFrom.name || prettyNumber(props.call.forwardedFrom.number)}</span>
      )}
      <div className="call-actions">
        <Button
          variant="danger"
          icon={isCall ? 'phone' : 'x'}
          className="call-decline"
          disabled={busy}
          onClick={() => void (props.kind === 'call' ? decline(props.call.id) : declineRing(props.ring))}
        >
          Decline
        </Button>
        <Button variant="primary" icon={isCall ? 'phone' : 'headset'} className="call-answer" disabled={busy} onClick={() => void answerIt()}>
          {label}
        </Button>
      </div>
    </div>
  );
}

/**
 * The person's desk phone answered a Patch's ring (Live sessions, 1.13.0): it's part of the Patch,
 * not a second call. Open goes to its conversation; Move here joins from this device, and the desk
 * phone hangs up once the app is in.
 */
function DeskPatch({ d, tenant }: { d: DeskInPatch; tenant: string }) {
  const navigate = useNavigate();
  const [moving, setMoving] = useState(false);
  const url = `/t/${encodeURIComponent(tenant)}/messages/${encodeURIComponent(d.conversationId)}`;
  return (
    <div className="call-card call-desk desk-patch" role="status" aria-label="Your desk phone is in a Patch">
      <div className="call-head">
        <span className="call-ring" aria-hidden="true">
          <Icon name="headset" size={17} />
        </span>
        <span className="call-what">Patch on your desk phone</span>
      </div>
      <span className="call-who">
        <strong>{d.with.length ? `With ${namesText(d.with)}` : 'In a Patch'}</strong>
        <span>Your desk phone is in it</span>
      </span>
      <div className="call-actions">
        <Button variant="secondary" onClick={() => navigate(url)}>
          Open
        </Button>
        <Button
          variant="primary"
          icon="headset"
          disabled={moving}
          onClick={async () => {
            setMoving(true);
            try {
              await patchIn({ tenant, conversationId: d.conversationId, title: d.with.length ? namesText(d.with) : 'Patch' });
            } finally {
              setMoving(false);
            }
          }}
        >
          {moving ? 'Moving…' : 'Move here'}
        </Button>
      </div>
    </div>
  );
}

/** A Patch started in one of this person's channels: a quiet card (no ring) with Join. */
function PatchStarted({ r }: { r: PatchRing }) {
  return (
    <div className="call-card call-notice patch-started" role="status">
      <span className="patch-started-text">
        <Icon name="headset" size={16} />
        <span>
          <strong>{r.by.name}</strong> started a Patch in #{r.title}
        </span>
      </span>
      <Button size="small" variant="secondary" onClick={() => void joinRing(r)}>
        Join
      </Button>
      <button type="button" className="call-x" aria-label="Dismiss" onClick={() => stopRing(r.key)}>
        <Icon name="x" size={15} />
      </button>
    </div>
  );
}

/** A missed call: who and when, Call back (in the app, when calls are on here), or see the list. */
function MissedCall({ n, canCall }: { n: MissedNotice; canCall: boolean }) {
  const navigate = useNavigate();
  const face = usePictureOfExtension(n.tenant, n.number);
  const at = new Date(n.at);
  const when = Number.isFinite(at.getTime()) ? at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
  const back = dialable(n.number);
  const callBack = async () => {
    if (!back) return;
    dismissMissed(n.callId);
    await dial(back);
  };
  const list = () => {
    dismissMissed(n.callId);
    navigate(`/t/${encodeURIComponent(n.tenant)}/me/calls`);
  };
  return (
    <div className="call-card vm-card missed-card" role="group" aria-label={`Missed call from ${n.caller}`}>
      <div className="call-head">
        {face ? (
          <Face userId={face.userId} name={n.caller} size="small" className="call-face" />
        ) : (
          <span className="vm-mark missed-mark" aria-hidden="true">
            <Icon name="phone" size={18} />
          </span>
        )}
        <span className="call-who">
          <strong>{n.caller}</strong>
          <span>Missed call{when ? ` · ${when}` : ''}</span>
        </span>
        <button type="button" className="call-x" aria-label="Dismiss" onClick={() => dismissMissed(n.callId)}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <div className="call-actions">
        {canCall && back && (
          <Button size="small" variant="primary" icon="phone" onClick={() => void callBack()}>
            Call back
          </Button>
        )}
        <Button size="small" variant="ghost" icon="chevRight" onClick={list}>
          My calls
        </Button>
      </div>
    </div>
  );
}

/** A new voicemail: who called and how long, Play it right here, or Open the list. */
function NewVoicemail({ n }: { n: VoicemailNotice }) {
  const navigate = useNavigate();
  // From a coworker: their photo.
  const face = usePictureOfExtension(n.tenant, n.number);
  const phone = usePhone();
  const audio = useRef<HTMLAudioElement | null>(null);
  const marked = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => () => playedVoicemail(n.tenant, n.messageId, false), [n.tenant, n.messageId]);
  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (!a.paused) return a.pause();
    setProblem('');
    playedVoicemail(n.tenant, n.messageId, true);
    a.play().then(
      () => {
        // Played: heard on the phone system too (as the voicemail list does), once.
        if (marked.current) return;
        marked.current = true;
        void api.changeVoicemail(n.tenant, n.messageId, { heard: true }).then(
          () => announceHere({ tenant: n.tenant }),
          () => undefined
        );
      },
      () => setProblem('It can’t play here. Open your voicemail instead.')
    );
  };
  const open = () => {
    dismissVoicemail(n.messageId);
    const base = `/t/${encodeURIComponent(n.tenant)}`;
    navigate(phone ? `${base}/me/calls?show=voicemail` : `${base}/me#voicemail`);
  };
  return (
    <div className="call-card vm-card" role="group" aria-label={`New voicemail from ${n.caller}`}>
      <div className="call-head">
        {face ? (
          <Face userId={face.userId} name={n.caller} size="small" className="call-face" />
        ) : (
          <span className="vm-mark" aria-hidden="true">
            <Icon name="voicemail" size={18} />
          </span>
        )}
        <span className="call-who">
          <strong>{n.caller}</strong>
          <span>New voicemail · {clockOf(n.duration)}</span>
        </span>
        <button type="button" className="call-x" aria-label="Dismiss" onClick={() => dismissVoicemail(n.messageId)}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <div className="call-actions">
        <Button size="small" variant="primary" icon={playing ? 'pause' : 'play'} onClick={toggle}>
          {playing ? 'Pause' : 'Play'}
        </Button>
        <Button size="small" variant="ghost" icon="chevRight" onClick={open}>
          Open voicemail
        </Button>
      </div>
      {problem && <p className="vm-problem">{problem}</p>}
      <audio
        ref={audio}
        src={api.voicemailAudioUrl(n.tenant, n.messageId)}
        preload="none"
        onPlay={() => {
          setPlaying(true);
          playedVoicemail(n.tenant, n.messageId, true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          heardVoicemail(n.tenant, n.messageId);
        }}
      />
    </div>
  );
}

/** Who it is: their name (a coworker's from the directory) over the number, or the number alone. */
function Who({ call, tenant }: { call: AppCall; tenant?: string }) {
  const number = prettyNumber(call.number);
  // A coworker calling: their photo, when they have one.
  const face = usePictureOfExtension(tenant ?? '', tenant ? call.number : null);
  const name = whoOf(call);
  const who = (
    <span className="call-who">
      <strong>{name}</strong>
      {name !== number && <span>{number}</span>}
    </span>
  );
  if (!face) return who;
  return (
    <span className="call-who-row">
      <Face userId={face.userId} name={name} size="small" className="call-face" />
      {who}
    </span>
  );
}

function Incoming({ call, s }: { call: AppCall; s: CallsState }) {
  const busy = s.busy !== null;
  if (call.patchOffer) return <PatchCallBack call={call} s={s} />;
  return (
    <div className="call-card call-incoming" role="alertdialog" aria-label={`Incoming call from ${call.name || prettyNumber(call.number)}`}>
      <div className="call-head">
        <span className="call-ring" aria-hidden="true">
          <Icon name="phone" size={17} />
        </span>
        <span className="call-what">{call.waiting || s.mine ? 'Another call' : 'Incoming call'}</span>
      </div>
      <Who call={call} tenant={s.tenant} />
      {call.forwardedFrom && (
        <span className="call-sub">Forwarded from {call.forwardedFrom.name || prettyNumber(call.forwardedFrom.number)}</span>
      )}
      <div className="call-actions">
        <Button variant="danger" icon="phone" className="call-decline" disabled={busy} onClick={() => void decline(call.id)}>
          Decline
        </Button>
        <Button variant="primary" icon="phone" className="call-answer" disabled={busy} onClick={() => void answer(call.id)}>
          {s.busy === `answer:${call.id}` ? 'Answering…' : s.mine ? 'Hold and answer' : 'Answer'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Someone a Patch rang, calling back while it's going (1.12.18): "Dana is calling. Bring Dana into
 * the Patch?" Bring into Patch, Answer (an ordinary call), or Decline.
 */
function PatchCallBack({ call, s }: { call: AppCall; s: CallsState }) {
  const busy = s.busy !== null;
  const name = call.patchOffer!.name;
  const first = name.trim().split(/\s+/)[0] || name;
  return (
    <div className="call-card call-incoming patch-callback" role="alertdialog" aria-label={`${name} is calling. Bring ${first} into the Patch?`}>
      <div className="call-head">
        <span className="call-ring" aria-hidden="true">
          <Icon name="headset" size={17} />
        </span>
        <span className="call-what">Call back to your Patch</span>
      </div>
      <Who call={call} tenant={s.tenant} />
      <span className="call-sub">
        {first} is calling. Bring {first} into the Patch?
      </span>
      <div className="call-actions">
        <Button variant="danger" icon="phone" className="call-decline" disabled={busy} onClick={() => void decline(call.id)}>
          Decline
        </Button>
        <Button variant="secondary" icon="phone" disabled={busy} onClick={() => void answer(call.id)}>
          {s.busy === `answer:${call.id}` ? 'Answering…' : 'Answer'}
        </Button>
        <Button variant="primary" icon="headset" className="call-answer" disabled={busy} onClick={() => void bringIntoPatch(call.id)}>
          {s.busy === `patch:${call.id}` ? 'Bringing in…' : 'Bring into Patch'}
        </Button>
      </div>
    </div>
  );
}

function stateText(call: AppCall, s: CallsState, secs: number): string {
  if (s.audio === 'reconnecting') return 'Reconnecting…';
  if (call.state === 'dialing') return call.event.type === 'ringing' || call.event.type === 'early_media' ? 'Ringing…' : 'Calling…';
  if (call.held) return `On hold · ${clockText(secs)}`;
  if (call.remoteHeld) return `They put you on hold · ${clockText(secs)}`;
  if (s.audio === 'connecting') return 'Connecting the sound…';
  return clockText(secs);
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
const LETTERS: Record<string, string> = { '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL', '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ', '0': '+' };

/** The keypad on a call: each key sends its tone (on a call with several people, to everyone not on hold). */
function Keys({ id }: { id: string }) {
  return (
    <div className="call-keys" role="group" aria-label="Keypad">
      {KEYS.map((k) => (
        <button key={k} type="button" className="call-key" onClick={() => pressKey(id, k)} aria-label={`Key ${k}`}>
          <span>{k}</span>
        </button>
      ))}
    </div>
  );
}

/** Mute and Unmute, for this device's microphone on the call (on the folded bar, the microphone alone). */
function MuteButton({ s, compact }: { s: CallsState; compact?: boolean }) {
  const off = s.muted || Boolean(s.micProblem);
  if (compact) {
    return (
      <Button size="small" variant={off ? 'secondary' : 'ghost'} icon={off ? 'micOff' : 'mic'} aria-pressed={off} aria-label={off ? 'Unmute' : 'Mute'} title={off ? 'Unmute' : 'Mute'} className="call-bar-btn" disabled={s.audio === 'off'} onClick={() => void toggleMute()} />
    );
  }
  return (
    <Button size="small" variant={off ? 'secondary' : 'ghost'} icon={off ? 'micOff' : 'mic'} aria-pressed={off} disabled={s.audio === 'off'} onClick={() => void toggleMute()}>
      {off ? 'Unmute' : 'Mute'}
    </Button>
  );
}

/** Set as the call card folds or opens from its arrow: the arrow on the other side takes the focus. */
const callFocus = { current: false };

/** A button that takes the focus when it appears because the call card was folded or opened from the other one. */
function useTakesFocus() {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!callFocus.current) return;
    callFocus.current = false;
    ref.current?.focus();
  }, []);
  return ref;
}

/** The arrow on the call card that folds it to one bar (remembered on this device). */
function FoldCallButton({ onFold }: { onFold: () => void }) {
  const ref = useTakesFocus();
  return (
    <button ref={ref} type="button" className="btn ghost small call-fold" aria-expanded="true" aria-label="Fold the call card" title="Fold the call card" onClick={onFold}>
      <Icon name="chev" size={15} strokeWidth={2.2} />
    </button>
  );
}

/**
 * The call card folded to one slim bar, for room while on a call: whether it's live, who, the
 * clock, Muted and On hold, how many more calls wait on hold, Mute and Hang up. Clicking the bar
 * (or its arrow) opens the card again, with the keypad, Hold, Transfer and Add someone. With
 * several people on the call, its red button ends it for everyone, as the card's does.
 */
function CallBar({ call, people, s, waiting, onOpen }: { call: AppCall; people: AppCall[]; s: CallsState; waiting: number; onOpen: () => void }) {
  const several = people.length > 1;
  const secs = useCallClock(confKey(several ? people[0]! : call));
  const ref = useTakesFocus();
  const connected = several ? people.filter((c) => c.state === 'active') : [call];
  const held = connected.length > 0 && connected.every((c) => c.held);
  const live = several ? anyoneTalking(people[0]!) && !held : !call.held;
  const muted = s.muted || Boolean(s.micProblem);
  // The call the buttons act on: with several people, any of them does for everyone.
  const id = several ? (s.mine && people.some((c) => c.id === s.mine) ? s.mine : (connected[0] ?? people[0]!).id) : call.id;
  const who = several ? `${people.length} people` : whoOf(call);
  const clock = s.audio === 'reconnecting' ? 'Reconnecting…' : clockText(secs);
  const end = several ? 'End for everyone' : 'Hang up';
  return (
    <div
      className="call-card call-mine call-bar"
      role="group"
      aria-label={callBarLabel({ who, clock, muted, held, waiting })}
      onClick={(e) => {
        if (!(e.target instanceof Element && e.target.closest('button'))) onOpen();
      }}
    >
      <span className={`call-dot${live ? ' live' : ''}`} aria-hidden="true" />
      <span className="call-who">
        <strong>{several ? peopleText(people) : who}</strong>
        <span className="call-bar-line">
          <span className="call-bar-clock">{clock}</span>
          {muted && <span className="call-bar-tag muted">Muted</span>}
          {held && <span className="call-bar-tag">On hold</span>}
          {waiting > 0 && (
            <span className="call-bar-tag" title={`${waiting} more ${waiting === 1 ? 'call' : 'calls'} on hold`}>
              +{waiting}
            </span>
          )}
        </span>
      </span>
      <MuteButton s={s} compact />
      <Button
        size="small"
        variant="danger"
        icon="phoneOff"
        className="call-bar-btn call-bar-end"
        aria-label={end}
        title={end}
        disabled={several && s.busy !== null}
        onClick={() => void (several ? endForEveryone(id) : hangup(call.id))}
      />
      <button ref={ref} type="button" className="btn ghost small call-bar-btn call-unfold" aria-expanded="false" aria-label="Open the call card" title="Open the call card" onClick={onOpen}>
        <Icon name="chev" size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/** Opens the speaker and microphone choice, when there's something to choose (two or more of either). */
function SoundButton({ s }: { s: CallsState }) {
  const d = useAudioDevices();
  const speakers = d.canChooseSpeaker && d.speakers.length > 1;
  if (s.audio === 'off' || (!speakers && d.mics.length < 2)) return null;
  const label = speakers ? 'Speaker and microphone' : 'Microphone';
  return (
    <Button size="small" variant={s.sound ? 'secondary' : 'ghost'} icon="headset" aria-pressed={s.sound} aria-label={label} title={label} className="call-sound-btn" onClick={() => showSound(!s.sound)} />
  );
}

/** The speaker or headset (where this browser can choose one) and the microphone, kept for calls and Patches. */
function SoundPanel() {
  const d = useAudioDevices();
  const speakers = d.canChooseSpeaker && d.speakers.length > 1;
  return (
    <div className="call-sound" role="group" aria-label={speakers ? 'Speaker and microphone' : 'Microphone'}>
      {speakers && (
        <label className="call-sound-row">
          <span>Speaker</span>
          <select className="input" value={chosenValue(d.speakers, d.speaker)} onChange={(e) => chooseDevice('audiooutput', e.target.value)} aria-label="Speaker">
            {choices(d.speakers).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {d.mics.length > 1 && (
        <label className="call-sound-row">
          <span>Microphone</span>
          <select className="input" value={chosenValue(d.mics, d.mic)} onChange={(e) => chooseDevice('audioinput', e.target.value)} aria-label="Microphone">
            {choices(d.mics).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <small className="call-sub">Kept on this {typeof navigator !== 'undefined' && /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'phone' : 'computer'} for calls and Patches.</small>
    </div>
  );
}

/** What the call's sound needs from the person (a click to hear it), and what went wrong with it. */
function SoundNotes({ s }: { s: CallsState }) {
  return (
    <>
      {s.needsClick && (
        <Button size="small" icon="volume" onClick={() => void startCallSound()}>
          Click to hear the call
        </Button>
      )}
      {s.micProblem && <p className="call-problem">{s.micProblem}</p>}
      {s.deviceNotice && (
        <p className="call-sub call-device-notice" role="status">
          {s.deviceNotice}
        </p>
      )}
    </>
  );
}

function OnCall({ call, s, caller, onFold }: { call: AppCall; s: CallsState; caller: AppCall | undefined; onFold?: () => void }) {
  // A call that had others on it keeps the call's clock (from when it began).
  const secs = useCallClock(confKey(call));
  const active = call.state === 'active';
  const consulting = Boolean(call.consultFor);
  useEffect(() => loadCoworkers(call.tenant), [call.tenant]);
  return (
    <div className="call-card call-mine" aria-label="Your call">
      {consulting && (
        <div className="call-transfer-bar" role="group" aria-label="Transfer">
          <span>
            Transferring <strong>{caller ? whoOf(caller) : 'the caller'}</strong> (on hold)
          </span>
          <div className="call-actions">
            <Button size="small" variant="ghost" disabled={s.busy !== null} onClick={() => void cancelTransfer(call)}>
              {s.busy === `cancel:${call.id}` ? 'Going back…' : 'Cancel'}
            </Button>
            <Button size="small" variant="primary" icon="arrowOut" disabled={!active || s.busy !== null} onClick={() => void completeTransfer(call)}>
              {s.busy === `complete:${call.id}` ? 'Transferring…' : 'Complete transfer'}
            </Button>
          </div>
        </div>
      )}
      <div className="call-head">
        <span className={`call-dot${active && !call.held ? ' live' : ''}`} aria-hidden="true" />
        <Who call={call} />
        <span className="call-clock" aria-live="polite">
          {stateText(call, s, secs)}
        </span>
        <SoundButton s={s} />
        {onFold && <FoldCallButton onFold={onFold} />}
      </div>
      {call.emergency && <span className="call-sub">Emergency call: tell them where you are.</span>}
      <SoundNotes s={s} />
      <div className="call-actions">
        <MuteButton s={s} />
        <Button size="small" variant={s.keypad ? 'secondary' : 'ghost'} icon="keypad" aria-pressed={s.keypad} disabled={!active} onClick={() => showKeypad(!s.keypad)}>
          Keypad
        </Button>
        <Button size="small" variant={call.held ? 'secondary' : 'ghost'} icon={call.held ? 'play' : 'pause'} aria-pressed={call.held} disabled={!active || s.busy !== null} onClick={() => void holdOrResume(call.id, !call.held)}>
          {call.held ? 'Resume' : 'Hold'}
        </Button>
        {!consulting && (
          <Button size="small" variant={s.transferring ? 'secondary' : 'ghost'} icon="arrowOut" aria-pressed={s.transferring} disabled={!active || s.busy !== null} onClick={() => showTransfer(!s.transferring)}>
            Transfer
          </Button>
        )}
        {!consulting && (
          <Button size="small" variant={s.adding ? 'secondary' : 'ghost'} icon="plus" aria-pressed={s.adding} disabled={!active || s.busy !== null} onClick={() => showAdd(!s.adding)}>
            Add someone
          </Button>
        )}
        <Button size="small" variant="danger" icon="phoneOff" className="call-hangup" onClick={() => void hangup(call.id)}>
          {call.state === 'dialing' ? 'Cancel' : 'Hang up'}
        </Button>
      </div>
      {s.keypad && active && <Keys id={call.id} />}
      {s.transferring && active && !consulting && <TransferPanel call={call} s={s} />}
      {s.adding && active && !consulting && <AddPanel id={call.id} s={s} />}
      {s.sound && <SoundPanel />}
    </div>
  );
}

/** Everyone on the call as one clock: on hold when all of them are. */
function conferenceText(people: AppCall[], s: CallsState, secs: number): string {
  if (s.audio === 'reconnecting') return 'Reconnecting…';
  const on = people.filter((c) => c.state === 'active');
  if (on.length && on.every((c) => c.held)) return `On hold · ${clockText(secs)}`;
  if (s.audio === 'connecting') return 'Connecting the sound…';
  return clockText(secs);
}

/** "Call with 2 people": the people on a call besides the person. */
const peopleText = (people: AppCall[]) => `Call with ${people.length} people`;

/**
 * This device's call with several people on it (the person added someone): everyone listed, with
 * Remove (Cancel while it rings) for each one added; Mute, Keypad, Hold (everyone), Add someone,
 * Leave (the others carry on) and End for everyone.
 */
function OnConference({ people, s, onFold }: { people: AppCall[]; s: CallsState; onFold?: () => void }) {
  const key = confKey(people[0]!);
  const secs = useCallClock(key);
  const connected = people.filter((c) => c.state === 'active');
  const active = connected.length > 0;
  const anyHeld = connected.some((c) => c.held);
  // The call the buttons act on: any of them does for everyone (the gateway takes the whole call).
  const id = s.mine && people.some((c) => c.id === s.mine) ? s.mine : (connected[0] ?? people[0]!).id;
  const full = people.length >= CONFERENCE_MAX;
  useEffect(() => loadCoworkers(people[0]!.tenant), [people]);
  return (
    <div className="call-card call-mine call-conference" role="group" aria-label={`Your call with ${people.length} people`}>
      <div className="call-head">
        <span className={`call-dot${active && anyoneTalking(people[0]!) ? ' live' : ''}`} aria-hidden="true" />
        <span className="call-who">
          <strong>{peopleText(people)}</strong>
        </span>
        <span className="call-clock" aria-live="polite">
          {conferenceText(people, s, secs)}
        </span>
        <SoundButton s={s} />
        {onFold && <FoldCallButton onFold={onFold} />}
      </div>
      <ul className="call-people" aria-label="On the call">
        {people.map((c) => (
          <Person key={c.id} call={c} s={s} />
        ))}
      </ul>
      <SoundNotes s={s} />
      <div className="call-actions">
        <MuteButton s={s} />
        <Button size="small" variant={s.keypad ? 'secondary' : 'ghost'} icon="keypad" aria-pressed={s.keypad} disabled={!active} onClick={() => showKeypad(!s.keypad)}>
          Keypad
        </Button>
        <Button size="small" variant={anyHeld ? 'secondary' : 'ghost'} icon={anyHeld ? 'play' : 'pause'} aria-pressed={anyHeld} disabled={!active || s.busy !== null} onClick={() => void holdOrResume(id, !anyHeld)}>
          {anyHeld ? 'Resume' : 'Hold'}
        </Button>
        <Button
          size="small"
          variant={s.adding ? 'secondary' : 'ghost'}
          icon="plus"
          aria-pressed={s.adding}
          disabled={!active || full || s.busy !== null}
          title={full ? `A call can have up to ${CONFERENCE_MAX} people besides you` : undefined}
          onClick={() => showAdd(!s.adding)}
        >
          Add someone
        </Button>
        <Button size="small" variant="secondary" icon="logout" disabled={s.busy !== null} title="Leave: the others carry on" onClick={() => void leaveCall(id)}>
          {s.busy === `leave:${id}` ? 'Leaving…' : 'Leave'}
        </Button>
        <Button size="small" variant="danger" icon="phoneOff" className="call-hangup" disabled={s.busy !== null} onClick={() => void endForEveryone(id)}>
          {s.busy === `end:${id}` ? 'Ending…' : 'End for everyone'}
        </Button>
      </div>
      {s.keypad && active && <Keys id={id} />}
      {s.adding && active && !full && <AddPanel id={id} s={s} />}
      {s.sound && <SoundPanel />}
    </div>
  );
}

/** One person on a call with several: who, how it's going (ringing, on hold), and Remove for those added. */
function Person({ call, s }: { call: AppCall; s: CallsState }) {
  const face = usePictureOfExtension(call.tenant, call.number);
  const who = whoOf(call);
  const number = prettyNumber(call.number);
  const ringing = call.state === 'dialing';
  const status = ringing
    ? call.event.type === 'ringing' || call.event.type === 'early_media'
      ? 'Ringing…'
      : 'Calling…'
    : call.held
      ? 'On hold'
      : call.remoteHeld
        ? 'Has you on hold'
        : null;
  const sub = [who !== number ? number : null, status].filter(Boolean).join(' · ');
  return (
    <li className={`call-person${ringing ? ' ringing' : ''}`} aria-label={`${who}${status ? `, ${status.replace('…', '')}` : ''}`}>
      {face ? <Face userId={face.userId} name={who} size="small" className="call-face" /> : <span className={`call-dot${ringing ? '' : ' live'}`} aria-hidden="true" />}
      <span className="call-who">
        <strong>{who}</strong>
        {sub && <span>{sub}</span>}
      </span>
      {call.added && (
        <Button size="small" variant="ghost" icon="x" disabled={s.busy !== null} aria-label={`${ringing ? 'Stop calling' : 'Remove'} ${who}`} onClick={() => void removeFromCall(call)}>
          {ringing ? 'Cancel' : 'Remove'}
        </Button>
      )}
    </li>
  );
}

/** Someone to bring into the call: a coworker's extension (suggested by name) or any number. */
function AddPanel({ id, s }: { id: string; s: CallsState }) {
  const [to, setTo] = useState('');
  const ok = dialable(to) !== null;
  const people = coworkers(s.tenant).filter((p) => p.extension !== s.extension);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) void addToCall(id, to).then((added) => added && setTo(''));
  };
  return (
    <form className="call-transfer call-add" onSubmit={submit} aria-label="Add someone to the call">
      <label className="sr-only" htmlFor={`add-${id}`}>
        Number or extension to add
      </label>
      <input
        id={`add-${id}`}
        className="input"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder="Number or extension"
        value={to}
        maxLength={40}
        autoFocus
        list={people.length ? `add-people-${id}` : undefined}
        onChange={(e) => setTo(e.target.value)}
      />
      {people.length > 0 && (
        <datalist id={`add-people-${id}`}>
          {people.map((p) => (
            <option key={p.extension} value={p.extension} label={p.name} />
          ))}
        </datalist>
      )}
      <div className="call-actions">
        <Button size="small" variant="ghost" type="button" onClick={() => showAdd(false)}>
          Cancel
        </Button>
        <Button size="small" variant="primary" type="submit" icon="plus" disabled={!ok || s.busy !== null}>
          {s.busy === `add:${id}` ? 'Calling…' : 'Add'}
        </Button>
      </div>
      <small className="call-sub">They’re called from your line. When they answer, everyone’s on the call together.</small>
    </form>
  );
}

/** Where the call goes: straight on (blind), or after talking to them first (warm). */
function TransferPanel({ call, s }: { call: AppCall; s: CallsState }) {
  const [to, setTo] = useState('');
  const ok = dialable(to) !== null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ok) void transfer(call.id, to);
  };
  return (
    <form className="call-transfer" onSubmit={submit} aria-label="Transfer the call">
      <label className="sr-only" htmlFor={`xfer-${call.id}`}>
        Number or extension to transfer to
      </label>
      <input
        id={`xfer-${call.id}`}
        className="input"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder="Number or extension"
        value={to}
        maxLength={40}
        autoFocus
        onChange={(e) => setTo(e.target.value)}
      />
      <div className="call-actions">
        <Button size="small" variant="ghost" type="button" disabled={!ok || s.busy !== null} onClick={() => void consult(call.id, to)}>
          {s.busy === `consult:${call.id}` ? 'Calling…' : 'Talk first'}
        </Button>
        <Button size="small" variant="primary" type="submit" icon="arrowOut" disabled={!ok || s.busy !== null}>
          {s.busy === `transfer:${call.id}` ? 'Transferring…' : 'Transfer'}
        </Button>
      </div>
      <small className="call-sub">Transfer sends them straight on. Talk first puts them on hold while you call.</small>
    </form>
  );
}

/**
 * Another of this device's calls, on hold: switch to it (the current one goes on hold). One with
 * several people on it says so, and ends for everyone.
 */
function Parked({ people, s }: { people: AppCall[]; s: CallsState }) {
  const call = people[0]!;
  const several = people.length > 1;
  const who = several ? peopleText(people) : whoOf(call);
  return (
    <div className="call-card call-parked" role="group" aria-label={`On hold: ${who}`}>
      <span className="call-dot" aria-hidden="true" />
      <span className="call-who">
        <strong>{who}</strong>
        <span>{several ? `${namesText(people.map(whoOf))} · On hold` : 'On hold'}</span>
      </span>
      <div className="call-actions">
        <Button size="small" variant="ghost" icon="play" disabled={s.busy !== null} onClick={() => void switchTo(call.id)}>
          {s.busy === `switch:${call.id}` ? 'Switching…' : 'Switch'}
        </Button>
        {several ? (
          <Button size="small" variant="danger" icon="phoneOff" disabled={s.busy !== null} aria-label={`End the call with ${namesText(people.map(whoOf))} for everyone`} onClick={() => void endForEveryone(call.id)}>
            End
          </Button>
        ) : (
          <Button size="small" variant="danger" icon="phoneOff" disabled={s.busy !== null} aria-label={`Hang up on ${who}`} onClick={() => void hangup(call.id)}>
            Hang up
          </Button>
        )}
      </div>
    </div>
  );
}

/** A call on another of this person's devices (with everyone on it, when there are several). */
function Elsewhere({ people }: { people: AppCall[] }) {
  const call = people[0]!;
  const key = confKey(call);
  const active = people.some((c) => c.state === 'active');
  const secs = useCallClock(active ? key : null);
  const several = people.length > 1;
  return (
    <div className="call-card call-elsewhere" role="status">
      <span className="call-dot live" aria-hidden="true" />
      <span className="call-who">
        <strong>{several ? peopleText(people) : whoOf(call)}</strong>
        {several && <span>{namesText(people.map(whoOf))}</span>}
        <span>{active ? `On another device · ${clockText(secs)}` : 'Calling from another device…'}</span>
      </span>
    </div>
  );
}

/** A call with several people that this person left: going on without them, on their line. End for everyone ends it. */
function LeftCall({ people, s }: { people: AppCall[]; s: CallsState }) {
  const call = people[0]!;
  const secs = useCallClock(confKey(call));
  return (
    <div className="call-card call-elsewhere call-left" role="group" aria-label={`Going on without you: ${namesText(people.map(whoOf))}`}>
      <span className="call-dot live" aria-hidden="true" />
      <span className="call-who">
        <strong>{namesText(people.map(whoOf))}</strong>
        <span>Going on without you · {clockText(secs)}</span>
      </span>
      <Button size="small" variant="danger" icon="phoneOff" disabled={s.busy !== null} onClick={() => void endForEveryone(call.id)}>
        End for everyone
      </Button>
    </div>
  );
}

/** Re-renders every second while `on` (a clock on screen). */
function useSecondTick(on: boolean): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [on]);
}

/**
 * A call on the person's own desk phone (or the phone system's own app), not one in Stitch: who
 * it's with and for how long, with Hold or Resume, Transfer and Hang up where Stitch may change
 * calls at their company (Resume and Transfer 1.13.0, task 2758, as their own portal sends them).
 */
function DeskCall({ c, p }: { c: PhoneCallView; p: PhoneCallsState }) {
  const ringing = c.state === 'ringing';
  const [moving, setMoving] = useState(false);
  useSecondTick(!ringing);
  const secs = Math.max(0, (c.seconds ?? 0) + Math.round((Date.now() - p.readAt) / 1000));
  const who = c.name || prettyNumber(c.number);
  const busy = p.busy !== null;
  const where = ringing ? (c.inbound ? 'Ringing on your desk phone' : 'Calling from your desk phone…') : c.state === 'held' ? 'On hold on your desk phone' : 'On your desk phone';
  return (
    <div className="call-card call-desk" role="group" aria-label={`${where}: ${who}`}>
      <div className="call-head">
        <span className={`call-dot${c.state === 'talking' ? ' live' : ''}`} aria-hidden="true" />
        <span className="call-who">
          <strong>{who}</strong>
          {c.name && c.number && <span>{prettyNumber(c.number)}</span>}
        </span>
        {!ringing && (
          <span className="call-clock" aria-hidden="true">
            {clockText(secs)}
          </span>
        )}
      </div>
      <span className="call-sub call-desk-where">
        <Icon name="phone" size={13} /> {where}
        {c.state === 'held' && !p.control && '. Take it off hold there.'}
      </span>
      {p.control && !ringing && (
        <div className="call-actions">
          {c.state === 'held' ? (
            <Button size="small" variant="ghost" icon="play" disabled={busy} onClick={() => void controlPhoneCall(c.id, 'resume')}>
              {p.busy === `resume:${c.id}` ? 'Resuming…' : 'Resume'}
            </Button>
          ) : (
            <Button size="small" variant="ghost" icon="pause" disabled={busy} onClick={() => void controlPhoneCall(c.id, 'hold')}>
              {p.busy === `hold:${c.id}` ? 'Holding…' : 'Hold'}
            </Button>
          )}
          <Button size="small" variant="ghost" icon="arrowOut" disabled={busy} aria-expanded={moving} onClick={() => setMoving((m) => !m)}>
            Transfer
          </Button>
          <Button size="small" variant="danger" icon="phoneOff" className="call-hangup" disabled={busy} onClick={() => void controlPhoneCall(c.id, 'hangup')}>
            {p.busy === `hangup:${c.id}` ? 'Hanging up…' : 'Hang up'}
          </Button>
        </div>
      )}
      {p.control && !ringing && moving && <DeskTransfer c={c} p={p} onDone={() => setMoving(false)} />}
    </div>
  );
}

/** Where a desk-phone call goes: straight on (a blind transfer, as the person's own portal does it). */
function DeskTransfer({ c, p, onDone }: { c: PhoneCallView; p: PhoneCallsState; onDone: () => void }) {
  const [to, setTo] = useState('');
  const ok = dialable(to) !== null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (ok && (await controlPhoneCall(c.id, 'transfer', dialable(to)!))) onDone();
  };
  return (
    <form className="call-transfer" onSubmit={(e) => void submit(e)} aria-label="Transfer the call on your desk phone">
      <label className="sr-only" htmlFor={`desk-xfer-${c.id}`}>
        Number or extension to transfer to
      </label>
      <input
        id={`desk-xfer-${c.id}`}
        className="input"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder="Number or extension"
        value={to}
        maxLength={40}
        autoFocus
        onChange={(e) => setTo(e.target.value)}
      />
      <div className="call-actions">
        <Button size="small" variant="ghost" type="button" disabled={p.busy !== null} onClick={onDone}>
          Cancel
        </Button>
        <Button size="small" variant="primary" type="submit" icon="arrowOut" disabled={!ok || p.busy !== null}>
          {p.busy === `transfer:${c.id}` ? 'Transferring…' : 'Transfer'}
        </Button>
      </div>
      <small className="call-sub">They go straight on, and the call leaves your desk phone.</small>
    </form>
  );
}

/**
 * The dial pad (My phone, and the phone app's Calls), laid out like a phone's own: the number large
 * at the top, round keys, a round green Call button with delete beside it. Hold 0 for +. On a call
 * already, Call puts that one on hold first. Says plainly when calls in the app aren't on yet.
 */
export function DialPad({ compact }: { compact?: boolean }) {
  const s = useCalls();
  const [number, setNumber] = useState('');
  const press = useRef<number | null>(null);
  const mine = s.calls.find((c) => c.id === s.mine);
  if (!s.tenant || !s.on) return null;
  if (!s.available) {
    return (
      <div className="dialpad off">
        <Icon name="phone" size={18} />
        <p>{s.reason ?? OFF.appCalling}</p>
        {!compact && <AskStitch tenant={s.tenant} feature="app-calling" compact />}
      </div>
    );
  }
  const ok = dialable(number) !== null;
  const add = (k: string) => setNumber((n) => (n + k).slice(0, 40));
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ok || s.busy) return;
    // Cleared once the call is placed, unless another number was typed meanwhile.
    const dialed = number;
    if (await dial(dialed)) setNumber((n) => (n === dialed ? '' : n));
  };
  // 0 held down for half a second types + (for a full international number).
  const down = (k: string) => {
    if (k !== '0') return;
    press.current = window.setTimeout(() => {
      press.current = -1;
      add('+');
    }, 500);
  };
  const up = (k: string) => {
    if (press.current === -1) {
      press.current = null;
      return;
    }
    if (press.current !== null) window.clearTimeout(press.current);
    press.current = null;
    add(k);
  };
  return (
    <form className={`dialpad${compact ? ' compact' : ''}`} onSubmit={(e) => void submit(e)} aria-label="Make a call">
      <input
        className="dialpad-number"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        placeholder="Number or extension"
        aria-label="Number or extension to call"
        value={number}
        maxLength={40}
        onChange={(e) => setNumber(e.target.value)}
      />
      <div className="dialpad-keys">
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            className="dialpad-key"
            aria-label={`Key ${k}`}
            onPointerDown={() => down(k)}
            onPointerUp={() => up(k)}
            onPointerLeave={() => {
              if (press.current !== null && press.current !== -1) window.clearTimeout(press.current);
              press.current = null;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                add(k);
              }
            }}
          >
            <span>{k}</span>
            {LETTERS[k] && <small aria-hidden="true">{LETTERS[k]}</small>}
          </button>
        ))}
      </div>
      <div className="dialpad-foot">
        <span aria-hidden="true" />
        <button type="submit" className="dialpad-call" aria-label="Call" aria-busy={s.busy === 'dial'} disabled={!ok || Boolean(s.busy)}>
          <Icon name="phone" size={28} strokeWidth={2} />
        </button>
        {number ? (
          <button type="button" className="dialpad-del" aria-label="Delete the last digit" onClick={() => setNumber((n) => n.slice(0, -1))}>
            <Icon name="backspace" size={24} />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>
      {s.busy === 'dial' && <p className="dialpad-note">Calling…</p>}
      {mine && s.busy !== 'dial' && <p className="dialpad-note">You’re on a call: it goes on hold while you call.</p>}
    </form>
  );
}
